import { createPermission } from '@backstage/plugin-permission-common';
import { stringifyEntityRef, DEFAULT_NAMESPACE } from '@backstage/catalog-model';

/** What a role can do in the backoffice itself. */
export type BackofficePermission =
  | 'viewing'
  | 'configuration'
  | 'handle_customer_data'
  | 'reports'
  | 'manage_backoffice_users'
  | 'manage_hub_users'
  | 'toggle_feature_flags';

export const BACKOFFICE_PERMISSIONS = {
  viewing: createPermission({
    name: 'backoffice.viewing',
    attributes: { action: 'read' },
  }),
  configuration: createPermission({
    name: 'backoffice.configuration',
    attributes: { action: 'update' },
  }),
  handle_customer_data: createPermission({
    name: 'backoffice.handle-customer-data',
    attributes: { action: 'read' },
  }),
  reports: createPermission({
    name: 'backoffice.reports',
    attributes: { action: 'read' },
  }),
  manage_backoffice_users: createPermission({
    name: 'backoffice.manage-users',
    attributes: { action: 'update' },
  }),
  manage_hub_users: createPermission({
    name: 'backoffice.manage-hub-users',
    attributes: { action: 'update' },
  }),
  toggle_feature_flags: createPermission({
    name: 'backoffice.feature-flags',
    attributes: { action: 'update' },
  }),
} as const;

const PERMISSION_BY_NAME = new Map(
  (Object.keys(BACKOFFICE_PERMISSIONS) as BackofficePermission[]).map(key => [
    BACKOFFICE_PERMISSIONS[key].name,
    key,
  ]),
);

export const backofficePermissionKey = (
  permissionName: string,
): BackofficePermission | undefined => PERMISSION_BY_NAME.get(permissionName);

export const isBackofficePermission = (
  value: string,
): value is BackofficePermission =>
  (Object.keys(BACKOFFICE_PERMISSIONS) as string[]).includes(value);

/** A Google local-part or an entity ref, stored as `user:default/<name>`. */
export const toUserEntityRef = (username: string): string => {
  const trimmed = username.trim();
  if (trimmed.startsWith('user:')) return trimmed;
  return stringifyEntityRef({
    kind: 'User',
    namespace: DEFAULT_NAMESPACE,
    name: trimmed,
  });
};

export const isGraphQLMutation = (query: string): boolean => {
  const withoutStrings = query
    .replace(/"(?:\\.|[^"\\])*"/g, '')
    .replace(/'(?:\\.|[^'\\])*'/g, '')
    .replace(/#[^\n]*/g, '');
  return /\bmutation\b/.test(withoutStrings);
};
